/**
 * Cache of per-event-transform proxy wrappers (EID-86), extracted out of
 * data-provider so the cache-hit/eviction contract is unit-testable without
 * mounting DataProvider (see repo testing note: kept dependency-free, no
 * 'framework'/'utils' imports).
 *
 * Event-using transforms must re-run whenever the interaction event changes.
 * BaseWorldLayer.cachedQuery dedupes by (query, source identity), and a map
 * click changes neither — so consumers are handed a fresh wrapper *identity*
 * per event (keyed on the current event), which busts that cache and re-runs
 * the transform with the new $inputs.$event. The wrapper delegates to the
 * live Transform so datasource/refresh stay current.
 *
 * EID-110 (M2/R8): stopTransformWatcher must call `delete(id)` when retiring
 * a transform. Without it, the cached {event,tx,proxy} tuple stayed
 * referenced here indefinitely after the registry entry was removed, pinning
 * the outgoing transform (and its input DataWrappers) in memory.
 */

export interface EventTransformSource {
  query: (q: unknown, squeeze?: boolean) => unknown;
  datasource: unknown;
  refresh: unknown;
  terminate: () => void;
}

export interface EventTransformProxy {
  query: (q: unknown, squeeze?: boolean) => unknown;
  readonly datasource: unknown;
  readonly refresh: unknown;
  terminate: () => void;
}

export interface EventTransformProxyCache {
  /** Number of cached proxies. Exposed for tests. */
  readonly size: number;
  /**
   * Return a cached proxy for `id` if it was built from the same `tx` and
   * `event`; otherwise build, cache, and return a fresh one.
   */
  get(
    id: string,
    tx: EventTransformSource,
    event: unknown,
  ): EventTransformProxy;
  /** Evict the cached proxy for `id` (called when its transform is retired). */
  delete(id: string): void;
}

interface CacheEntry {
  event: unknown;
  tx: EventTransformSource;
  proxy: EventTransformProxy;
}

export const createEventTransformProxyCache = (): EventTransformProxyCache => {
  const cache = new Map<string, CacheEntry>();

  return {
    get size() {
      return cache.size;
    },
    get(id, tx, event) {
      const cached = cache.get(id);
      if (cached && cached.event === event && cached.tx === tx) {
        return cached.proxy;
      }
      const proxy: EventTransformProxy = {
        query: (q, squeeze) => tx.query(q, squeeze),
        get datasource() {
          return tx.datasource;
        },
        get refresh() {
          return tx.refresh;
        },
        terminate: () => tx.terminate(),
      };
      cache.set(id, { event, tx, proxy });
      return proxy;
    },
    delete(id) {
      cache.delete(id);
    },
  };
};
